import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Plus, Settings, Trash2, UserPlus, Crown, Shield, User, X, Globe, Clock, Check, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const Organizations = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { activeOrg, setActiveOrg, hasMultipleOrgs, needsOrgSelection } = useActiveOrg();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(null);
    const [newOrg, setNewOrg] = useState({ name: '', slug: '', description: '', email: '', timezone: 'Europe/Istanbul' });
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleSelectOrg = (org) => {
        setActiveOrg(org);
        setSuccess(t('organizations.select_success', { name: org.name }));
        // 1 saniye sonra Dashboard'a yönlendir
        setTimeout(() => {
            navigate('/');
        }, 500);
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const [orgsRes, meRes] = await Promise.all([
                API.get(`/organizations/?_t=${Date.now()}`),
                API.get(`/me/?_t=${Date.now()}`).catch(() => ({ data: { is_superuser: false } }))
            ]);
            setOrganizations(orgsRes.data);
            setCurrentUser(meRes.data);
            setLoading(false);
        } catch (err) {
            console.error("Organizations fetch error:", err);
            setLoading(false);
        }
    };

    const handleMemberPrefToggle = async (member, field) => {
        try {
            const newValue = !member[field];
            const res = await API.patch(`/organization-members/${member.id}/`, {
                [field]: newValue
            });
            
            const updatedMembers = showDetailModal.members.map(m => {
                if (m.id === member.id) {
                    return res.data;
                }
                return m;
            });
            
            setShowDetailModal({
                ...showDetailModal,
                members: updatedMembers
            });
            setSuccess(t('organizations.success_member_updated', 'Üye bildirim tercihleri güncellendi.'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(t('organizations.error_member_update_failed', 'Üye güncellenemedi.'));
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleCreate = async () => {
        try {
            await API.post('/organizations/', newOrg);
            setShowModal(false);
            setNewOrg({ name: '', slug: '', description: '', email: '', timezone: 'Europe/Istanbul' });
            setSuccess(t('organizations.create_success'));
            fetchOrganizations();
        } catch (err) {
            setError(err.response?.data?.detail || t('organizations.create_failed'));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('organizations.delete_confirm'))) {
            try {
                await API.delete(`/organizations/${id}/`);
                setSuccess(t('organizations.delete_success'));
                fetchOrganizations();
            } catch (err) {
                setError(t('organizations.delete_failed'));
            }
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const locale = i18n.language && i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US';
        return new Date(dateStr).toLocaleDateString(locale);
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'owner': return <Crown size={14} className="text-yellow-500" />;
            case 'admin': return <Shield size={14} className="text-blue-500" />;
            default: return <User size={14} className="text-gray-500" />;
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('organizations.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('organizations.subtitle_manage')}</p>
                </div>
                {currentUser?.is_superuser && (
                    <button
                        onClick={() => setShowModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        {t('organizations.new_org_btn')}
                    </button>
                )}
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center text-red-700">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center text-green-700">
                    <span>{success}</span>
                    <button onClick={() => setSuccess(null)}><X size={18} /></button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {organizations.length === 0 ? (
                    <div className="col-span-full bg-white rounded-xl shadow-sm p-8 border border-gray-100 text-center">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">{t('organizations.no_orgs')}</p>
                    </div>
                ) : (
                    organizations.map((org) => {
                        const isActive = activeOrg?.id === org.id;
                        return (
                            <div
                                key={org.id}
                                className={`bg-white rounded-xl shadow-sm p-6 border-2 transition-all ${isActive
                                        ? 'border-green-500 ring-2 ring-green-100'
                                        : 'border-gray-100 hover:shadow-md hover:border-blue-200'
                                    }`}
                            >
                                {/* Active Badge */}
                                {isActive && (
                                    <div className="flex items-center gap-1 text-green-600 text-xs font-semibold mb-2">
                                        <Check size={14} />
                                        {t('common.activeOrg')}
                                    </div>
                                )}

                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-lg bg-blue-100">
                                        <Building2 className="w-6 h-6 text-blue-600" />
                                    </div>
                                    {currentUser?.is_superuser && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setShowDetailModal(org)}
                                                className="p-2 text-gray-400 hover:text-gray-600"
                                            >
                                                <Settings className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(org.id)}
                                                className="p-2 text-red-400 hover:text-red-600"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{org.name}</h3>
                                <p className="text-sm text-gray-500 mb-2">@{org.slug}</p>
                                {org.description && (
                                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{org.description}</p>
                                )}
                                <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                                    <div className="flex items-center">
                                        <Users className="w-4 h-4 mr-1" />
                                        <span>{t('organizations.members_count', { count: org.member_count || 0 })}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                            {t('organizations.robots_count', { count: org.robot_count || 0 })}
                                        </span>
                                    </div>
                                </div>
                                {org.owner && (
                                    <div className="mt-3 text-xs text-gray-400 flex items-center">
                                        <Crown size={12} className="mr-1 text-yellow-500" />
                                        {t('organizations.owner_label', { owner: org.owner })}
                                    </div>
                                )}

                                {/* Select Button */}
                                {!isActive && (
                                    <button
                                        onClick={() => handleSelectOrg(org)}
                                        className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        {t('organizations.select_org')}
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900">{showDetailModal.name}</h2>
                            <button onClick={() => setShowDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Genel Bilgiler */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">Slug</p>
                                    <p className="font-medium">@{showDetailModal.slug}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1 flex items-center"><Globe size={12} className="mr-1" /> {t('organizations.timezone')}</p>
                                    <p className="font-medium">{showDetailModal.timezone || 'UTC'}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1 flex items-center"><Clock size={12} className="mr-1" /> {t('organizations.created_at')}</p>
                                    <p className="font-medium">{formatDate(showDetailModal.created_at)}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-xs text-gray-500 mb-1">{t('common.status')}</p>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${showDetailModal.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {showDetailModal.is_active ? t('organizations.active') : t('organizations.inactive')}
                                    </span>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg col-span-2">
                                    <p className="text-xs text-gray-500 mb-1">{t('organizations.email')}</p>
                                    <p className="font-medium truncate text-blue-600">{showDetailModal.email || '-'}</p>
                                </div>
                            </div>

                            {showDetailModal.description && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-2">{t('organizations.description')}</h3>
                                    <p className="text-gray-600">{showDetailModal.description}</p>
                                </div>
                            )}

                            {/* Üyeler */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="font-semibold text-gray-900">{t('organizations.members_with_count', { count: showDetailModal.member_count || 0 })}</h3>
                                    <button className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                                        <UserPlus size={16} className="mr-1" /> {t('organizations.add_member')}
                                    </button>
                                </div>
                                {showDetailModal.members && showDetailModal.members.length > 0 ? (
                                    <div className="space-y-2">
                                        {showDetailModal.members.map(member => (
                                            <div key={member.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                                        <User size={16} className="text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{member.username}</p>
                                                        <p className="text-xs text-gray-500">{member.email}</p>
                                                        <div className="mt-2 flex gap-4 text-xs text-gray-500">
                                                            <label className="flex items-center space-x-1 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={member.notify_on_start || false}
                                                                    onChange={() => handleMemberPrefToggle(member, 'notify_on_start')}
                                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                                />
                                                                <span>{t('settings.notify_on_start', 'Başladı')}</span>
                                                            </label>
                                                            <label className="flex items-center space-x-1 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={member.notify_on_success || false}
                                                                    onChange={() => handleMemberPrefToggle(member, 'notify_on_success')}
                                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                                />
                                                                <span>{t('settings.notify_on_success', 'Başarılı')}</span>
                                                            </label>
                                                            <label className="flex items-center space-x-1 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={member.notify_on_failure || false}
                                                                    onChange={() => handleMemberPrefToggle(member, 'notify_on_failure')}
                                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                                    />
                                                                <span>{t('settings.notify_on_failure', 'Hatalı')}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {getRoleIcon(member.role)}
                                                    <span className="text-sm text-gray-600">{member.role_display}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">{t('organizations.no_members')}</p>
                                )}
                            </div>

                            {/* İstatistikler */}
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div className="text-center p-4 bg-blue-50 rounded-lg">
                                    <p className="text-3xl font-bold text-blue-600">{showDetailModal.member_count || 0}</p>
                                    <p className="text-sm text-gray-600">{t('organizations.member')}</p>
                                </div>
                                <div className="text-center p-4 bg-green-50 rounded-lg">
                                    <p className="text-3xl font-bold text-green-600">{showDetailModal.robot_count || 0}</p>
                                    <p className="text-sm text-gray-600">{t('organizations.robot')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('organizations.create_title')}</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('organizations.name_label')}</label>
                                <input
                                    type="text"
                                    value={newOrg.name}
                                    onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('organizations.name_placeholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('organizations.slug_label')}</label>
                                <input
                                    type="text"
                                    value={newOrg.slug}
                                    onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('organizations.slug_placeholder')}
                                />
                                <p className="text-xs text-gray-500 mt-1">{t('organizations.slug_help')}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('organizations.description_label')}</label>
                                <textarea
                                    value={newOrg.description}
                                    onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('organizations.description_placeholder')}
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('organizations.email_label')}</label>
                                <input
                                    type="email"
                                    value={newOrg.email}
                                    onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('organizations.email_placeholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('organizations.timezone_label')}</label>
                                <select
                                    value={newOrg.timezone}
                                    onChange={(e) => setNewOrg({ ...newOrg, timezone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                                    <option value="UTC">UTC</option>
                                    <option value="Europe/London">Europe/London</option>
                                    <option value="America/New_York">America/New_York</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleCreate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {t('organizations.create_btn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Organizations;
